import { useT } from '@transifex/react';
import type { ChangeEvent, JSX } from 'react';
import { useState, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { small } from 'pkg/config/breakpoints';

import DateTime from 'pkg/datetime';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';

import MaterialSymbol, { IconSizeMap } from 'components/material-symbols';

import { Calendar } from 'components/form/calendar';
import type { MarkedDateGroup } from 'components/form/calendar/types';
import { Field } from 'components/form/inputs/field';
import { Prefix, Suffix } from 'components/form/inputs/input_base';
import Row from 'components/layout/row';
import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';

import * as ContextMenu from 'design/context_menu';

import * as css from './styles.css';

interface Props {
	hideTimeInput?: boolean;
	hideValue?: boolean;
	// When true and hideTimeInput is true, sets the time to 23:59:59 instead of 00:00:00.
	// Use this for "until"/"to"/"end" date pickers where the intent is "through the end of this day".
	endOfDay?: boolean;
	onTimeChange?: (date: Date | null) => void;
	onDateChange?: (date: Date | null) => void;
	onRenderedDateChange?: (date: Date) => void;
	onTimeBlur?: () => void;
	date?: Date;

	disableDatesBefore?: Date;
	disableDatesAfter?: Date;
	placeholder?: string;

	dateFieldName?: string;
	dateReadonly?: boolean;
	dateRequired?: boolean;

	testid?: string;
	highlightedDates?: Date[][];
	markedDates?: MarkedDateGroup[];
	prefixIcon?: MaterialSymbolVariant;
	clearable?: boolean;
}

const formatDateForInput = (date: Date): string => {
	// Native date inputs require a 4-digit year; an unpadded year (e.g. "206")
	// makes the input reject the value and render as empty.
	const year = String(date.getFullYear()).padStart(4, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

/**
 * DateTimePicker component
 *
 * A date and time selection component that displays a calendar for date picking
 * and an optional time input field. Integrates with custom calendar and field components.
 */

export function DateTimePicker({
	hideTimeInput = false,
	hideValue = false,
	endOfDay = false,
	onTimeChange,
	onDateChange,
	onRenderedDateChange,
	onTimeBlur,
	date,
	disableDatesBefore,
	disableDatesAfter,
	placeholder,

	dateFieldName,
	dateReadonly = false,
	dateRequired = false,

	highlightedDates,
	markedDates,
	prefixIcon,
	testid,
	clearable = false,
}: Props): JSX.Element {
	const t = useT();
	const methods = useFormContext();

	const getInitialDate = () => date ?? null;

	const [selectedDate, setSelectedDate] = useState<Date | null>(getInitialDate);
	const [renderedDate, setRenderedDate] = useState<Date>(
		() => date ?? new Date()
	);
	const [showCalendar, setShowCalendar] = useState(false);

	const initial = getInitialDate();

	const [inputValue, setInputValue] = useState(() => {
		return initial ? formatDateForInput(initial) : '';
	});

	const [isEditing, setIsEditing] = useState(false);

	const displayDate = selectedDate ?? new Date();

	const [timeInputValue, setTimeInputValue] = useState(() =>
		new DateTime(displayDate).toTimeInputString()
	);
	const [isEditingTime, setIsEditingTime] = useState(false);

	const dateRef = useRef<HTMLInputElement>(null);

	const updateFormValue = (value: string) => {
		if (dateFieldName && methods) {
			methods.setValue(dateFieldName, value, {
				shouldValidate: true,
				shouldDirty: true,
			});
		}
	};

	// Commit an API-provided default into RHF on mount so a prefilled date
	// validates. Only when `date` is set, so we never commit a value the user
	// hasn't chosen (e.g. Safari's phantom today's-date placeholder).
	useComponentDidMount(() => {
		if (date && dateFieldName && methods) {
			methods.setValue(dateFieldName, formatDateForInput(date));
		}
	});

	const handleClear = () => {
		setSelectedDate(null);
		setInputValue('');
		setTimeInputValue('00:00');

		if (dateFieldName && methods) {
			methods.setValue(dateFieldName, '');
		}

		onDateChange?.(null);
		onTimeChange?.(null);
		closeCalendar();
	};

	// Track the prop's underlying time so the sync effect only fires on real
	// prop value changes — not on reference churn from parent re-renders, and
	// not when isEditing flips (which would overwrite user-picked values after
	// the calendar closes).
	const lastSyncedPropTime = useRef<number | null>(
		date ? date.getTime() : null
	);
	const propTime = date ? date.getTime() : null;

	useEffect(() => {
		if (propTime === lastSyncedPropTime.current) {
			return;
		}
		lastSyncedPropTime.current = propTime;
		if (date) {
			if (!isEditing) {
				setInputValue(formatDateForInput(date));
				setSelectedDate(date);
			}
			if (!isEditingTime) {
				setTimeInputValue(new DateTime(date).toTimeInputString());
			}
		}
	}, [propTime, date, isEditing, isEditingTime]);

	const handleFocus = () => {
		setIsEditing(true);
		setShowCalendar(true);
	};

	const isDateInAllowedRange = (date: Date): boolean => {
		const dateOnly = new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate()
		);

		if (disableDatesBefore) {
			const minDate = new Date(disableDatesBefore);
			minDate.setHours(0, 0, 0, 0);
			if (dateOnly < minDate) return false;
		}

		if (disableDatesAfter) {
			const maxDate = new Date(disableDatesAfter);
			maxDate.setHours(0, 0, 0, 0);
			if (dateOnly > maxDate) return false;
		}

		return true;
	};

	const handleDateInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;

		// Don't update state for empty values (happens during typing in native date inputs)
		if (!value) return;

		setInputValue(value);

		// Validate format: must be YYYY-MM-DD with all numeric parts
		const datePattern = /^\d{4}-\d{2}-\d{2}$/;
		if (!datePattern.test(value)) return;

		const [yearStr, monthStr, dayStr] = value.split('-');
		const year = Number.parseInt(yearStr, 10);
		const month = Number.parseInt(monthStr, 10);
		const day = Number.parseInt(dayStr, 10);

		if (month < 1 || month > 12) return;

		const lastDayOfMonth = new Date(year, month, 0).getDate();

		if (day < 1 || day > lastDayOfMonth) return;

		let newDate: Date;

		if (hideTimeInput) {
			const [h, m, s] = endOfDay ? [23, 59, 59] : [0, 0, 0];
			newDate = new Date(year, month - 1, day, h, m, s);
		} else {
			newDate = new Date(displayDate);
			newDate.setFullYear(year, month - 1, day);
		}

		// Validate against disabled date constraints
		if (!isDateInAllowedRange(newDate)) return;

		setInputValue(value);

		setSelectedDate(newDate);
		setRenderedDate(newDate);
		updateFormValue(formatDateForInput(newDate));
		onDateChange?.(newDate);
	};

	const handleDateInputBlur = () => {
		// On blur, revert to the last valid selected date if current input is invalid
		if (inputValue) {
			const [yearStr, monthStr, dayStr] = inputValue.split('-');
			const inputDate = new Date(
				Number.parseInt(yearStr, 10),
				Number.parseInt(monthStr, 10) - 1,
				Number.parseInt(dayStr, 10)
			);
			if (!isDateInAllowedRange(inputDate)) {
				// Revert to last valid date, or clear if none exists
				setInputValue(selectedDate ? formatDateForInput(selectedDate) : '');
			}
		}
		handleBlur();
	};

	const handleTimeInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		setTimeInputValue(event.target.value);
	};

	const handleTimeInputFocus = () => {
		setIsEditingTime(true);
	};

	const handleTimeInputBlur = () => {
		setIsEditingTime(false);
		const [h, m] = timeInputValue.split(':').map((p) => Number.parseInt(p, 10));

		if (!Number.isNaN(h) && !Number.isNaN(m)) {
			const nextDate = new Date(displayDate.getTime());
			nextDate.setHours(h, m, 0);
			onTimeChange?.(nextDate);
		} else {
			// Revert to valid time if input is invalid
			setTimeInputValue(new DateTime(displayDate).toTimeInputString());
		}

		onTimeBlur?.();
	};

	const calendarChange = (dates: Date[]) => {
		let newDate: Date;

		if (hideTimeInput) {
			const d = dates[0];
			const [h, m, s] = endOfDay ? [23, 59, 59] : [0, 0, 0];
			newDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, s);
		} else {
			const newDateTime = dates[0].setHours(
				displayDate.getHours(),
				displayDate.getMinutes()
			);
			newDate = new Date(newDateTime);
		}

		const formattedDate = formatDateForInput(newDate);
		setSelectedDate(newDate);
		setInputValue(formattedDate);
		updateFormValue(formattedDate);
		onDateChange?.(newDate);
		closeCalendar();
	};

	const closeCalendar = () => {
		if (document.activeElement === dateRef.current) {
			dateRef.current.blur();
		}

		setShowCalendar(false);
	};

	const handleBlur = () => {
		setIsEditing(false);
	};

	const handleRenderedDateChange = (date: Date) => {
		setRenderedDate(date);
		onRenderedDateChange?.(date);
	};

	const showClearButton =
		clearable && selectedDate !== null && !dateReadonly && !hideValue;

	return (
		<div>
			<Row
				columns={
					hideTimeInput ? '1fr' : `1fr ${small ? 'max-content' : 'auto'}`
				}>
				<Field
					type="date"
					className={prefixIcon ? css.dateField : undefined}
					value={hideValue ? '' : inputValue}
					onChange={handleDateInputChange}
					onFocus={handleFocus}
					onBlur={handleDateInputBlur}
					ref={dateRef}
					name={dateFieldName}
					placeholder={placeholder}
					testid={testid}
					readOnly={dateReadonly}
					required={dateRequired}
					min={
						disableDatesBefore
							? formatDateForInput(disableDatesBefore)
							: undefined
					}
					max={
						disableDatesAfter
							? formatDateForInput(disableDatesAfter)
							: undefined
					}>
					{prefixIcon && (
						<Prefix grayFill>
							<MaterialSymbol variant={prefixIcon} />
						</Prefix>
					)}
					{showClearButton && (
						<Suffix onClick={handleClear} className={css.suffix}>
							<MaterialSymbol size={IconSizeMap.default} variant="close" />
						</Suffix>
					)}
				</Field>
				{!hideTimeInput && (
					<Field
						placeholder={t(`Select time`)}
						onChange={handleTimeInputChange}
						onFocus={handleTimeInputFocus}
						onBlur={handleTimeInputBlur}
						value={hideValue ? '' : timeInputValue}
						type="time"
					/>
				)}
			</Row>
			{showCalendar && !dateReadonly && (
				<div className={css.calendarWrapper}>
					<ContextMenu.Menu
						toggleWith={<span className={css.calendarToggle} />}
						defaultOpen={true}
						onClose={closeCalendar}>
						<ContextMenu.Pane padding="0">
							<Calendar
								selectedDates={selectedDate ? [selectedDate] : []}
								onDateChange={calendarChange}
								disableDatesBefore={disableDatesBefore}
								disableDatesAfter={disableDatesAfter}
								onRenderedDateChange={handleRenderedDateChange}
								highlightedDates={highlightedDates}
								markedDates={markedDates}
								renderedDate={renderedDate}
							/>
						</ContextMenu.Pane>
					</ContextMenu.Menu>
				</div>
			)}
		</div>
	);
}
